// =============================================================================
// FILE: packages/shared/src/config/feature.registry.ts
// LAYER: Foundation — Feature Visibility System (Core UX Control Layer)
// PURPOSE: Central registry of every user-visible feature. The renderer
//          reads this to gate Sidebar entries and Command Bar commands;
//          the main process reads the same registry to refuse activation
//          on locked / disabled modules (defence-in-depth — never trust
//          the UI gate alone).
//
//          State semantics:
//            ACTIVE       → normal interaction
//            DISABLED     → dimmed + tooltip explanation (admin can toggle)
//            LOCKED       → lock icon + access message (License Server gated)
//            EXPERIMENTAL → visually marked, opt-in for stability boundary
//
//          Architecture layer (3-Layer System):
//            core   → non-revocable offline engine (cannot be locked)
//            gated  → License-Server feature-gated module
//            cloud  → optional Cloud / AI / Services layer (revocable)
// =============================================================================

export type FeatureState =
  | 'ACTIVE'
  | 'DISABLED'
  | 'LOCKED'
  | 'EXPERIMENTAL';

export type ArchitectureLayer =
  | 'core'
  | 'gated'
  | 'cloud';

export interface FeatureEntry {
  /** Stable id used by Sidebar route, Command Bar command, and main-side checks. */
  readonly id: string;
  /** Human label rendered in UI. */
  readonly label: string;
  /** One-line description for tooltips + License Server entitlements. */
  readonly description: string;
  /** Current state. May be overridden at runtime by License Server. */
  readonly state: FeatureState;
  /** Architecture layer per the 3-Layer principle. */
  readonly layer: ArchitectureLayer;
  /** Shown when the user hovers a non-ACTIVE feature. */
  readonly inactiveTooltip?: string;
}

// ---------------------------------------------------------------------------
// Static registry — Phase 1 + Phase 2 foundation.
// AI modules are flagged EXPERIMENTAL per stability + future licensing rule.
// ---------------------------------------------------------------------------
export const FEATURE_REGISTRY: ReadonlyArray<FeatureEntry> = [
  // -- Core (offline, non-revocable) ----------------------------------------
  { id: 'dashboard',     label: 'Dashboard',     description: 'Live operations overview.',                         state: 'ACTIVE',       layer: 'core'  },
  { id: 'buildings',     label: 'Buildings',     description: 'Buildings hierarchy + Revit linkage.',              state: 'ACTIVE',       layer: 'core'  },
  { id: 'projects',      label: 'Projects',      description: 'Project lifecycle and Project Folder System.',      state: 'ACTIVE',       layer: 'core'  },
  { id: 'racks',         label: 'Racks',         description: 'Rack elevation editor and U-position assignments.', state: 'ACTIVE',       layer: 'core'  },
  { id: 'documents',     label: 'Documents',     description: 'Document register + SharePoint binding.',           state: 'ACTIVE',       layer: 'core'  },
  { id: 'doc-control',   label: 'Document Control', description: 'Authoritative document registry (MVP, read-only).', state: 'ACTIVE',    layer: 'core'  },
  { id: 'assets',        label: 'Assets',        description: 'Asset register and IFC import.',                    state: 'ACTIVE',       layer: 'core'  },
  { id: 'cabling',       label: 'Cabling',       description: 'Port-level connectivity graph and trace.',          state: 'ACTIVE',       layer: 'core'  },
  { id: 'field',         label: 'Field',         description: 'Field visit workflow + captures.',                  state: 'ACTIVE',       layer: 'core'  },
  { id: 'propagation',   label: 'Propagation',   description: 'Global Change Propagation Engine (GCPE).',          state: 'ACTIVE',       layer: 'core'  },
  { id: 'qaqc',          label: 'QA/QC',         description: 'QAQC gate (Phase 1 validation).',                   state: 'ACTIVE',       layer: 'core'  },
  { id: 'sync',          label: 'Sync',          description: 'Local + SharePoint sync queue.',                    state: 'ACTIVE',       layer: 'core'  },
  { id: 'settings',      label: 'Settings',      description: 'Workspace, integrations, and admin controls.',      state: 'ACTIVE',       layer: 'core'  },
  { id: 'health',        label: 'Health',        description: 'System health probes.',                             state: 'ACTIVE',       layer: 'core'  },
  { id: 'backup',        label: 'Backup',        description: 'Encrypted local backups + restore.',                state: 'ACTIVE',       layer: 'core'  },

  // -- Gated (License Server controlled — currently ACTIVE pending server) --
  { id: 'cost',          label: 'Cost Templates', description: 'Company cost templates + project estimating.',     state: 'ACTIVE',       layer: 'gated' },
  { id: 'comms',         label: 'Comms',         description: 'Suggest-mode email follow-ups.',                    state: 'ACTIVE',       layer: 'gated' },

  // -- Cloud / AI (revocable — flagged EXPERIMENTAL pending stability bar) --
  { id: 'ai',            label: 'AI Assistant',   description: 'Local AI assistant — stability-track preview.',     state: 'EXPERIMENTAL', layer: 'cloud',
    inactiveTooltip: 'AI features are stability-track previews. Behaviour may change between releases.' },
  { id: 'coach-validation', label: 'Coach',       description: 'Project Coach validation (read-only summary).',     state: 'EXPERIMENTAL', layer: 'cloud',
    inactiveTooltip: 'Coach is a stability-track preview backed by the AI provider port.' },
  { id: 'autopilot',     label: 'Autopilot',     description: 'Plan-only Project Autopilot.',                       state: 'EXPERIMENTAL', layer: 'cloud',
    inactiveTooltip: 'Autopilot plans are stability-track previews. All actions require explicit approval.' },
  { id: 'agent',         label: 'Agent',         description: 'AI agent goals + capability composition.',           state: 'EXPERIMENTAL', layer: 'cloud',
    inactiveTooltip: 'Agent projects are stability-track previews. Plans require explicit approval before execution.' },
  { id: 'sie',           label: 'Smart Ingest',  description: 'Smart Ingestion Engine — paste text, extract structured data.', state: 'ACTIVE', layer: 'core' },
  { id: 'auto-report',   label: 'Auto-Report',   description: 'Project Health & Activity Report — billable proof of work.',   state: 'ACTIVE', layer: 'core' },
  { id: 'schedule',      label: 'Schedule',      description: 'Project schedule and milestone tracking.',           state: 'ACTIVE',       layer: 'core'  },
  { id: 'creator',       label: 'Creator',       description: 'About the platform creator.',                        state: 'ACTIVE',       layer: 'core'  },
];

// ---------------------------------------------------------------------------
// Index helpers
// ---------------------------------------------------------------------------
const BY_ID = new Map<string, FeatureEntry>(
  FEATURE_REGISTRY.map((f) => [f.id, f]),
);

export function getFeature(id: string): FeatureEntry | undefined {
  return BY_ID.get(id);
}

export function getFeatureState(id: string): FeatureState {
  return BY_ID.get(id)?.state ?? 'ACTIVE';
}

export function isFeatureInteractive(id: string): boolean {
  const s = getFeatureState(id);
  return s === 'ACTIVE' || s === 'EXPERIMENTAL';
}

export function listActiveFeatures(): ReadonlyArray<FeatureEntry> {
  return FEATURE_REGISTRY.filter((f) => f.state === 'ACTIVE');
}
